import { MeshLoadCancelledError } from '@/plugins/mesh/errors'
import { createMeshProgressReporter, describeMeshSource } from '@/plugins/mesh/loaders/loaderUtils'
import { resolveMeshOptimizationOptions } from '@/plugins/mesh/optimization/optimizationOptions'
import type {
  MeshLoadRequest,
  MeshLoadResult,
  MeshOptimizationOptionsInput,
  MeshOptimizationReport,
  MeshRuntimeOptimizer,
} from '@/plugins/mesh/types'
import type { AssetLoader, LoadContext } from '@/shared/types/assets'

function createInactiveReport(
  asset: MeshLoadResult,
  status: 'disabled' | 'fallback',
  failureReason?: string
): MeshOptimizationReport {
  const count = asset.primitives.length
  return {
    status,
    beforeBatchCount: count,
    afterBatchCount: count,
    instancedBatchCount: 0,
    instancedPrimitiveCount: 0,
    mergedBatchCount: 0,
    mergedPrimitiveCount: 0,
    skippedPrimitiveCount: count,
    drawCallReduction: 0,
    timings: {
      partitionMs: 0,
      batchingMs: 0,
      finalizeMs: 0,
      totalMs: 0,
    },
    failureReason,
  }
}

function withOptimizationReport(
  asset: MeshLoadResult,
  report: MeshOptimizationReport
): MeshLoadResult {
  return {
    ...asset,
    report: {
      ...asset.report,
      optimization: report,
    },
  }
}

/** 在格式 Loader 返回前组合可选运行时优化器。 */
export class OptimizingMeshLoader implements AssetLoader<MeshLoadRequest, MeshLoadResult> {
  readonly format: string
  readonly detectPriority?: number

  constructor(
    private readonly inner: AssetLoader<MeshLoadRequest, MeshLoadResult>,
    private readonly optimizer?: MeshRuntimeOptimizer,
    private readonly defaults?: MeshOptimizationOptionsInput
  ) {
    this.format = inner.format
    this.detectPriority = inner.detectPriority
  }

  canLoad(request: MeshLoadRequest): boolean {
    return this.inner.canLoad?.(request) ?? false
  }

  async load(request: MeshLoadRequest, context: LoadContext): Promise<MeshLoadResult> {
    const options = resolveMeshOptimizationOptions(this.defaults, request.optimization)
    if (!options.enabled || !this.optimizer) {
      const asset = await this.inner.load(request, context)
      if (asset.report.optimization?.appliedAt === 'loader') return asset
      return withOptimizationReport(asset, createInactiveReport(asset, 'disabled'))
    }

    const source = describeMeshSource(request)
    const signal = context.signal ?? request.signal
    const reportProgress = createMeshProgressReporter(request, context)
    const innerRequest = { ...request, onProgress: undefined }
    reportProgress(0)
    const asset = await this.inner.load(innerRequest, {
      ...context,
      signal,
      onProgress: (progress) => reportProgress(progress * 0.65),
    })

    if (signal?.aborted) {
      asset.dispose()
      throw new MeshLoadCancelledError(source, { cause: signal.reason })
    }

    const consumed = asset.consumedOptimization
    const remainingOptions = consumed
      ? {
          ...options,
          instance: options.instance && !consumed.instance,
          merge: options.merge && !consumed.merge,
          partition: {
            ...options.partition,
            enabled: options.partition.enabled && !consumed.partition,
          },
        }
      : options
    if (!remainingOptions.instance && !remainingOptions.merge) {
      reportProgress(1)
      return asset
    }

    try {
      const optimized = await this.optimizer.optimize(asset, {
        options: remainingOptions,
        signal,
        onProgress: (progress) => reportProgress(0.65 + progress * 0.35),
      })
      reportProgress(1)
      return optimized
    } catch (error) {
      if (signal?.aborted || error instanceof MeshLoadCancelledError) {
        asset.dispose()
        throw error instanceof MeshLoadCancelledError
          ? error
          : new MeshLoadCancelledError(source, { cause: signal?.reason ?? error })
      }
      if (options.failureMode === 'fallback') {
        reportProgress(1)
        const reason = error instanceof Error ? error.message : String(error)
        return withOptimizationReport(asset, createInactiveReport(asset, 'fallback', reason))
      }
      asset.dispose()
      throw error
    }
  }
}
